/**
 * Platform adapters (spec §2.3).
 *
 * Small interfaces that web and native both satisfy, so story and canvas
 * components never contain a platform check. Anything not yet implemented
 * reports `available: false` rather than pretending to succeed — a fake
 * success is explicitly forbidden (spec §0).
 */

import { CAPABILITIES } from '@/contracts/brand';

export interface ShareResult {
  status: 'shared' | 'saved' | 'cancelled' | 'unsupported';
}

export interface BillingProduct {
  key: string;
  displayPrice: string;
  period: 'month' | 'year' | 'once';
  title: string;
}

export interface PlatformAdapters {
  name: 'web' | 'ios';
  /** True when running inside the native shell. */
  isNative: boolean;
  connectivity: {
    online(): boolean;
    subscribe(fn: (online: boolean) => void): () => void;
  };
  lifecycle: {
    /** Fires when the app is backgrounded — the cue to flush a draft. */
    onPause(fn: () => void): () => void;
    onResume(fn: () => void): () => void;
  };
  share: {
    available: boolean;
    export(file: { blob: Blob; filename: string; title: string }): Promise<ShareResult>;
  };
  billing: {
    /** False until real products are configured (spec §16.1). */
    available: boolean;
    listProducts(): Promise<BillingProduct[]>;
    purchase(productKey: string): Promise<{ status: 'unavailable' }>;
    restore(): Promise<{ status: 'unavailable' }>;
  };
}

function makeConnectivity() {
  return {
    online: () => navigator.onLine,
    subscribe(fn: (online: boolean) => void) {
      const on = () => fn(true);
      const off = () => fn(false);
      window.addEventListener('online', on);
      window.addEventListener('offline', off);
      return () => {
        window.removeEventListener('online', on);
        window.removeEventListener('offline', off);
      };
    },
  };
}

function makeLifecycle() {
  return {
    onPause(fn: () => void) {
      const h = () => {
        if (document.visibilityState === 'hidden') fn();
      };
      document.addEventListener('visibilitychange', h);
      // pagehide is the reliable "app is going away" signal on iOS Safari.
      window.addEventListener('pagehide', fn);
      return () => {
        document.removeEventListener('visibilitychange', h);
        window.removeEventListener('pagehide', fn);
      };
    },
    onResume(fn: () => void) {
      const h = () => {
        if (document.visibilityState === 'visible') fn();
      };
      document.addEventListener('visibilitychange', h);
      return () => document.removeEventListener('visibilitychange', h);
    },
  };
}

const webAdapters: PlatformAdapters = {
  name: 'web',
  isNative: false,
  connectivity: makeConnectivity(),
  lifecycle: makeLifecycle(),
  share: {
    available: true,
    async export({ blob, filename, title }) {
      const file = new File([blob], filename, { type: blob.type });
      // Prefer the real share sheet where the browser has one.
      const nav = navigator as Navigator & {
        canShare?: (d: ShareData) => boolean;
        share?: (d: ShareData) => Promise<void>;
      };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        try {
          await nav.share({ files: [file], title });
          return { status: 'shared' };
        } catch (err) {
          // A cancelled share is not a failure and must not lose work (§10.4).
          if ((err as { name?: string })?.name === 'AbortError') return { status: 'cancelled' };
        }
      }
      // Some hosts (the sandboxed shared preview) discard downloads without
      // raising anything. Claiming "saved" there would be a lie the caller
      // then repeats to the child, so the capability is checked first.
      if (!CAPABILITIES.fileSave) return { status: 'unsupported' };
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      return { status: 'saved' };
    },
  },
  billing: {
    available: false,
    async listProducts() {
      return [];
    },
    async purchase() {
      return { status: 'unavailable' as const };
    },
    async restore() {
      return { status: 'unavailable' as const };
    },
  },
};

/**
 * The native shell swaps `share` for Capacitor Share + Filesystem, `lifecycle`
 * for real app-state events, and `billing` for the StoreKit bridge. Detection
 * is by Capacitor's own global, and the native module is imported lazily so
 * the web bundle carries no native code.
 */
function isNative(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() ?? false;
}

export const Platform: PlatformAdapters = isNative()
  ? { ...webAdapters, name: 'ios', isNative: true }
  : webAdapters;

/**
 * Upgrades the adapters in place once the native modules have loaded.
 * Called from the app entry point; on web it is a no-op.
 */
export async function initPlatform(): Promise<void> {
  if (!Platform.isNative) return;
  const native = await import('./native');
  const mutable = Platform as {
    share: PlatformAdapters['share'];
    lifecycle: PlatformAdapters['lifecycle'];
    billing: PlatformAdapters['billing'];
  };
  mutable.share = native.makeNativeShare();
  mutable.lifecycle = native.makeNativeLifecycle();
  mutable.billing = native.makeNativeBilling();
}
